<template>
  <div class="store-page">
    <!-- 表单容器 -->
    <el-form
      ref="storeForm"
      :model="form"
      :rules="rules"
      label-width="120px"
      class="store-form"
    >
      <!-- 门店名称（必填，≤8字） -->
      <el-form-item label="门店名称" prop="storeName">
        <el-input
          v-model="form.storeName"
          placeholder="请填写门店名称"
          maxlength="8"
          show-word-limit
        />
      </el-form-item>

      <!-- 门店Logo（必填，单张，1:1，≤100KB） -->
      <el-form-item label="门店Logo" prop="storeLogo">
        <div class="upload-area">
          <el-upload
            ref="uploadLogo"
            class="avatar-uploader"
            action="#"
            :show-file-list="false"
            :on-change="handleLogoChange"
            :before-upload="validateLogo"
            :auto-upload="false"
            :disabled="logoUploadDisabled"
          >
            <img v-if="form.storeLogo" :src="form.storeLogo" class="avatar" />
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，比例1:1，大小≤100KB</div>
          <el-button
            v-if="form.storeLogo"
            type="danger"
            size="mini"
            @click="removeLogo"
            class="delete-btn"
          >
            删除
          </el-button>
        </div>
      </el-form-item>

      <!-- 联系电话（必填，11位手机号） -->
      <el-form-item label="门店联系电话" prop="contactNumbers">
        <el-input
          v-model="form.contactNumbers"
          placeholder="请填写11位手机号"
          maxlength="11"
          show-word-limit
        />
      </el-form-item>

      <!-- 营业执照（选填，单张，≤100KB） -->
      <el-form-item label="营业执照">
        <div class="upload-area">
          <el-upload
            ref="uploadLicense"
            class="avatar-uploader"
            action="#"
            :show-file-list="false"
            :on-change="handleLicenseChange"
            :before-upload="validateFileSize"
            :auto-upload="false"
            :disabled="licenseUploadDisabled"
          >
            <img
              v-if="form.businessLicense"
              :src="form.businessLicense"
              class="avatar"
            />
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，大小≤100KB</div>
          <el-button
            v-if="form.businessLicense"
            type="danger"
            size="mini"
            @click="removeLicense"
            class="delete-btn"
          >
            删除
          </el-button>
        </div>
      </el-form-item>

      <!-- 其他证照（选填，最多5张，≤100KB） -->
      <el-form-item label="其他证照">
        <div class="upload-area">
          <el-upload
            action="#"
            list-type="picture-card"
            :file-list="certFileList"
            :on-change="handleCertChange"
            :on-remove="handleCertRemove"
            :before-upload="validateFileSize"
            :auto-upload="false"
            :limit="5"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="el-upload__tip">仅支持JPG/PNG，最多5张，大小≤100KB</div>
        </div>
      </el-form-item>

      <!-- 门店地址（简化：仅输入框，限制200字符） -->
      <el-form-item label="门店地址" prop="address">
        <el-input
          v-model="form.address"
          placeholder="请填写详细地址（例：湖北省宜昌市西陵区东苑街道珠海路6号半岛花园2期12号楼404）"
          maxlength="200"
          show-word-limit
          rows="3"
          type="textarea"
        />
      </el-form-item>

      <!-- 操作按钮 -->
      <el-form-item>
        <el-button type="primary" @click="submitForm">确认</el-button>
        <el-button @click="cancel">取消</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
export default {
  name: "AddStore",
  data() {
    return {
      form: {
        storeName: "",
        storeLogo: "",
        contactNumbers: "", // 与数据库一致的联系电话字段
        businessLicense: "",
        otherCertificates: [],
        address: "", // 仅字符输入，无地图关联
      },
      logoUploadDisabled: false,
      licenseUploadDisabled: false,
      certFileList: [],
      rules: {
        storeName: [
          { required: true, message: "请填写门店名称", trigger: "blur" },
          { max: 8, message: "门店名称最多8个字符", trigger: "blur" },
        ],
        storeLogo: [
          { required: true, message: "请上传门店Logo", trigger: "change" },
        ],
        contactNumbers: [
          { required: true, message: "请填写联系电话", trigger: "blur" },
          {
            pattern: /^1\d{10}$/,
            message: "请输入有效的11位手机号",
            trigger: "blur",
          },
        ],
        address: [
          { required: true, message: "请填写详细地址", trigger: "blur" },
          { max: 200, message: "详细地址最多200个字符", trigger: "blur" },
        ],
      },
    };
  },
  // 移除编辑相关的 mounted 钩子逻辑（无需加载编辑数据）
  mounted() {},
  methods: {
    // 【已删除】编辑功能专属方法：fetchStoreDetail（加载门店详情用于编辑）

    // Logo上传处理（仅保留新增逻辑，路径拼接正确的 /api 前缀）
    async handleLogoChange(file) {
      this.logoUploadDisabled = true;
      try {
        const isValid = await this.validateLogo(file.raw);
        if (!isValid) throw new Error("图片验证失败");
        const imageUrl = await this.uploadImage(file.raw);
        this.form.storeLogo = `/api${imageUrl}`; // 仅上传时拼接1次 /api，避免重复
        this.$message.success("门店Logo上传成功");
      } catch (error) {
        console.error("Logo上传错误:", error);
        let errorMsg = "门店Logo上传失败";
        if (error.response) {
          if (error.response.status === 401) {
            errorMsg = "未授权，请重新登录";
            this.$message.warning("请重新登录");
            this.$router.push("/merchantLogin");
          } else if (error.response.status === 404) {
            errorMsg = "上传接口不存在，请联系管理员";
          } else if (error.response.status === 413) {
            errorMsg = "图片太大，请选择小于100KB的图片";
          } else if (error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          }
        } else if (error.message.includes("Network Error")) {
          errorMsg = "网络错误，请检查网络连接";
        }
        this.$message.error(errorMsg);
        this.$refs.uploadLogo.clearFiles();
      } finally {
        this.logoUploadDisabled = false;
      }
    },

    // 营业执照上传处理（仅保留新增逻辑）
    async handleLicenseChange(file) {
      this.licenseUploadDisabled = true;
      try {
        const imageUrl = await this.uploadImage(file.raw);
        this.form.businessLicense = `/api${imageUrl}`; // 仅上传时拼接1次 /api
        this.$message.success("营业执照上传成功");
      } catch (error) {
        console.error("营业执照上传错误:", error);
        let errorMsg = "营业执照上传失败";
        if (error.response) {
          if (error.response.status === 401) {
            errorMsg = "未授权，请重新登录";
            this.$router.push("/merchantLogin");
          } else if (error.response.status === 404) {
            errorMsg = "上传接口不存在，请联系管理员";
          } else if (error.response.status === 413) {
            errorMsg = "图片太大，请选择小于100KB的图片";
          }
        }
        this.$message.error(errorMsg + "：" + error.message);
        this.$refs.uploadLicense.clearFiles();
      } finally {
        this.licenseUploadDisabled = false;
      }
    },

    // 其他证照上传处理（仅保留新增逻辑）
    async handleCertChange(file, fileList) {
      try {
        const imageUrl = await this.uploadImage(file.raw);
        const fullUrl = `/api${imageUrl}`; // 仅上传时拼接1次 /api
        this.form.otherCertificates.push(fullUrl);
        this.certFileList = fileList.map((f) =>
          f.uid === file.uid ? { ...f, url: fullUrl } : f
        );
        this.$message.success("证照上传成功");
      } catch (error) {
        console.error("证照上传错误:", error);
        let errorMsg = "证照上传失败";
        if (error.response) {
          if (error.response.status === 401) {
            errorMsg = "未授权，请重新登录";
            this.$router.push("/merchantLogin");
          } else if (error.response.status === 404) {
            errorMsg = "上传接口不存在，请联系管理员";
          }
        }
        this.$message.error(errorMsg + "：" + error.message);
        this.certFileList = fileList.filter((f) => f.uid !== file.uid);
      }
    },

    // 上传图片到服务器（通用上传逻辑，仅用于新增）
    async uploadImage(file) {
      const formData = new FormData();
      formData.append("image", file);
      const token = localStorage.getItem("token") || this.$store.state.token;
      try {
        const response = await this.$http.post(
          "/api/store/upload-image",
          formData,
          { headers: { Authorization: token ? `Bearer ${token}` : "" } }
        );
        if (response.data && response.data.imageUrl) {
          return response.data.imageUrl;
        } else {
          throw new Error(response.data?.message || "上传失败");
        }
      } catch (error) {
        console.error("上传图片失败:", error);
        throw error;
      }
    },

    // 提交表单（仅保留新增逻辑，删除编辑相关的PUT请求）
    async submitForm() {
      try {
        // 1. 表单验证
        await this.$refs.storeForm.validate();
        // 2. 登录状态校验
        const token = localStorage.getItem("token");
        if (!token) {
          this.$message.error("请先登录");
          this.$router.push("/merchantLogin");
          return;
        }
        // 3. 仅调用新增接口（删除编辑的PUT分支）
        const response = await this.$http.post("/api/addstore", this.form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // 4. 新增成功处理
        if (response.data.code === 0) {
          this.$message.success("门店新增成功！");
          this.$router.push("/StoreManage"); // 跳转回门店列表页
        } else {
          throw new Error(response.data.message || "新增失败");
        }
      } catch (error) {
        // 错误处理（保留原有逻辑）
        console.error("提交表单错误:", error);
        if (error.response && error.response.status === 401) {
          this.$message.error("登录已过期，请重新登录");
          this.$router.push("/merchantLogin");
        } else {
          this.$message.error(error.message || "提交失败，请重试");
        }
      }
    },

    // 其他基础方法（保留，无编辑关联）
    removeLogo() {
      this.form.storeLogo = "";
      this.$message.info("已删除门店Logo");
    },
    removeLicense() {
      this.form.businessLicense = "";
      this.$message.info("已删除营业执照");
    },
    validateLogo(file) {
      return new Promise((resolve) => {
        // 大小校验（≤100KB）
        if (file.size > 1024 * 100) {
          this.$message.error("Logo大小不能超过100KB");
          resolve(false);
          return;
        }
        // 比例校验（1:1）
        const img = new Image();
        img.onload = () => {
          if (img.width !== img.height) {
            this.$message.error("Logo比例必须为1:1");
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.onerror = () => {
          this.$message.error("图片加载失败，请选择有效的图片文件");
          resolve(false);
        };
        img.src = URL.createObjectURL(file);
      });
    },
    validateFileSize(file) {
      if (file.size > 1024 * 100) {
        this.$message.error("文件大小不能超过100KB");
        return false;
      }
      return true;
    },
    handleCertRemove(file, fileList) {
      const index = this.form.otherCertificates.findIndex(
        (url) => url === file.url
      );
      if (index !== -1) {
        this.form.otherCertificates.splice(index, 1);
      }
      this.certFileList = fileList;
    },
    cancel() {
      this.$router.back(); // 返回上一页
    },
  },
};
</script>

<style scoped>
.store-page {
  padding: 20px;
  background: #fff;
}

.store-form {
  max-width: 600px;
  margin: 0 auto;
}

.upload-area {
  margin-bottom: 15px;
}

.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 148px;
  height: 148px;
  display: inline-block;
  vertical-align: top;
  margin-right: 10px;
}

.avatar-uploader:hover {
  border-color: #409eff;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.avatar {
  width: 148px;
  height: 148px;
  display: block;
}

.delete-btn {
  margin-top: 10px;
}

/* 移除编辑相关的冗余样式（原map-placeholder样式保留，若后续需恢复地图可复用） */
.map-placeholder {
  height: 100px;
  line-height: 100px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 10px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  color: #8c939d;
}

.map-placeholder:hover {
  border-color: #409eff;
  color: #409eff;
}
</style>